import { FilterAlt, FilterAltOff, Search } from "@mui/icons-material";
import { Box, Button, Popover, TextField } from "@mui/material";
import { ReactNode, useState } from "react";

import InputGeneric from "../InputGeneric";
import { SelectComponent } from "../Select/Select";
import {
    ActionLeft,
    DateFilter,
    FilterContent,
    FilterStyled,
    SearchContent,
    SearchDiv,
} from "./styles";
import {
    CompleteFilterValues,
    DEFAULT_PERIODS,
    FilterFieldItem,
    FilterPeriodItem,
    FilterStatusItem,
} from "./types";

interface CompleteFilterProps {
    fields: FilterFieldItem[];
    statusItems?: FilterStatusItem[];
    statusParam?: string;
    statusLabel?: string;
    fieldOptions?: Record<string, FilterStatusItem[]>;
    periodsItems?: FilterPeriodItem[];
    showDateFilter?: boolean;
    startDateLabel?: string;
    onSearch: (query: string) => void;
    actions?: ReactNode;
    defaultField?: string;
}

function getDateToFormat(subDays: number) {
    const d = new Date();
    d.setDate(d.getDate() - subDays);
    return d.toISOString().substr(0, 10);
}

export function CompleteFilter({
    fields,
    statusItems,
    statusParam = "status",
    statusLabel = "Status",
    fieldOptions = {},
    periodsItems = DEFAULT_PERIODS,
    showDateFilter = false,
    startDateLabel = "Data de Criação Início",
    onSearch,
    actions,
    defaultField,
}: CompleteFilterProps) {
    const initialField = defaultField ?? fields[0]?.value ?? "";
    const [filter, setFilter] = useState<CompleteFilterValues>({
        filterType: initialField,
        filterValue: "",
        status: null,
        startDate: new Date("1970-01-01 00:00:00").toISOString().substr(0, 10),
        endDate: getDateToFormat(0),
        period: 20000,
    });

    const currentFieldOptions = fieldOptions[filter.filterType];

    function search() {
        let queryConstruct = "";

        if (
            filter.filterValue !== undefined &&
            filter.filterValue !== null &&
            String(filter.filterValue) !== ""
        ) {
            queryConstruct += `&${filter.filterType}=${filter.filterValue}`;
        }

        if (filter.status !== undefined && filter.status !== null) {
            queryConstruct += `&${statusParam}=${filter.status}`;
        }

        if (showDateFilter) {
            queryConstruct += `&start_date=${filter.startDate}&end_date=${filter.endDate}`;
        }

        onSearch(queryConstruct);
    }

    function handleFilterChange(
        event:
            | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | {
                  target: { name?: string; value: unknown };
              }
    ) {
        const { name, value } = event.target;
        if (!name) {
            return;
        }
        setFilter((prev) => {
            if (name === "filterType") {
                return {
                    ...prev,
                    filterType: String(value),
                    filterValue: "",
                };
            }
            return {
                ...prev,
                [name]: value,
            };
        });
    }

    function clearFilters() {
        const cleared: CompleteFilterValues = {
            filterType: initialField,
            filterValue: "",
            status: null,
            startDate: new Date("1970-01-01 00:00:00")
                .toISOString()
                .substr(0, 10),
            endDate: getDateToFormat(0),
            period: 20000,
        };
        setFilter(cleared);
        onSearch("");
    }

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    return (
        <FilterContent>
            <SearchDiv>
                <Button
                    variant="contained"
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    color="primary"
                >
                    Filtrar <FilterAlt />
                </Button>
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                >
                    <SearchContent>
                        {statusItems && statusItems.length > 0 && (
                            <FilterStyled>
                                <SelectComponent
                                    label={statusLabel}
                                    fullWidth
                                    items={statusItems}
                                    value={
                                        filter.status !== null &&
                                        filter.status !== undefined
                                            ? filter.status
                                            : ""
                                    }
                                    name="status"
                                    onChange={(e) => handleFilterChange(e)}
                                />
                            </FilterStyled>
                        )}
                        <Box>
                            <SelectComponent
                                label="Campo"
                                items={fields}
                                value={String(filter.filterType)}
                                onChange={(e) => handleFilterChange(e)}
                                name="filterType"
                                defaultValue={fields[0]?.value}
                            />
                            {currentFieldOptions ? (
                                <SelectComponent
                                    label="Pesquisa"
                                    items={currentFieldOptions}
                                    value={filter.filterValue}
                                    onChange={(e) => handleFilterChange(e)}
                                    name="filterValue"
                                    size={18}
                                />
                            ) : (
                                <TextField
                                    label="Pesquisa"
                                    variant="outlined"
                                    name="filterValue"
                                    size="small"
                                    className="margin"
                                    sx={{ width: "18rem" }}
                                    value={filter.filterValue}
                                    onChange={(e) => handleFilterChange(e)}
                                    onBlur={() => search()}
                                    onKeyUp={(e) => {
                                        if (e.key === "Enter") {
                                            search();
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        {showDateFilter && (
                            <>
                                <DateFilter className="margin">
                                    <InputGeneric
                                        fullWidth
                                        size="small"
                                        className="bigInput"
                                        type="date"
                                        label={startDateLabel}
                                        name="startDate"
                                        inputProps={{ maxLength: 10 }}
                                        onChange={(e) => handleFilterChange(e)}
                                        value={filter.startDate}
                                    />
                                    <InputGeneric
                                        fullWidth
                                        size="small"
                                        className="bigInput"
                                        type="date"
                                        label="Até"
                                        name="endDate"
                                        inputProps={{ maxLength: 10 }}
                                        onChange={(e) => handleFilterChange(e)}
                                        value={filter.endDate}
                                    />
                                </DateFilter>
                                <SelectComponent
                                    label="período"
                                    fullWidth
                                    items={periodsItems}
                                    value={Number(filter.period)}
                                    name="period"
                                    onChange={(e) => {
                                        setFilter({
                                            ...filter,
                                            period: Number(e.target.value),
                                            startDate: getDateToFormat(
                                                Number(e.target.value)
                                            ),
                                        });
                                    }}
                                />
                            </>
                        )}
                        <Box>
                            <Button
                                color="error"
                                variant="contained"
                                size="small"
                                onClick={() => clearFilters()}
                            >
                                Limpar filtros
                                <FilterAltOff
                                    fontSize="large"
                                    style={{ fontSize: "20px" }}
                                />
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => search()}
                                color="secondary"
                                size="small"
                            >
                                <Search />
                                Buscar
                            </Button>
                        </Box>
                    </SearchContent>
                </Popover>
            </SearchDiv>
            {actions && <ActionLeft>{actions}</ActionLeft>}
        </FilterContent>
    );
}
